/**
 * mSTEM Device Connection Modal container (GEN-03 / GEN-04). MOCK-ONLY.
 *
 * ONE reusable container shared by the Robot Manager and Kit Manager. Owns
 * the connection wizard's local phase machine + the mock progress timer.
 * NOT Redux-connected — it is a self-contained sub-flow rendered by each
 * manager container when the student presses "Kết nối".
 *
 * Phase machine:
 *   pick → form → connecting → success → (onClose)
 *
 * Mock progress: setInterval that bumps progress in fixed increments and,
 * on reaching 100%, flips to the 'success' phase. Leak-guarded —
 * componentWillUnmount clears the interval (covered by unit test). No real
 * getUserMedia/BLE/serial/network is ever touched.
 *
 * Lifted from the firmware-wizard timer pattern (Pitfall #7): we tick AT a
 * 300ms boundary so redux-throttle (300ms coalesce) never drops a frame —
 * though this container holds progress in local state (not Redux), keeping
 * the cadence identical keeps the demo visuals consistent across modals.
 */
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';

import DeviceConnectionModalComponent from '../components/device-connection-modal/device-connection-modal.jsx';

const TICK_INTERVAL_MS = 300;
const TICK_INCREMENT = 12;

class DeviceConnectionModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handlePickTransport',
            'handleFormChange',
            'handleStartConnect',
            'handleBack',
            'handleCancel',
            'tickProgress'
        ]);
        this.intervalId = null;
        this.state = {
            phase: 'pick',
            transport: null,
            progress: 0,
            form: {ssid: '', password: '', btDevice: '', usbPort: ''}
        };
    }
    componentWillUnmount () {
        this.clearTimer();
    }
    clearTimer () {
        if (this.intervalId !== null) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }
    handlePickTransport (transport) {
        this.setState({transport: transport, phase: 'form'});
    }
    handleFormChange (key, value) {
        this.setState(prev => ({form: Object.assign({}, prev.form, {[key]: value})}));
    }
    handleStartConnect () {
        this.clearTimer();
        this.setState({phase: 'connecting', progress: 0});
        this.intervalId = setInterval(this.tickProgress, TICK_INTERVAL_MS);
    }
    tickProgress () {
        this.setState(prev => {
            const next = Math.min(100, prev.progress + TICK_INCREMENT);
            if (next >= 100) {
                this.clearTimer();
                return {progress: 100, phase: 'success'};
            }
            return {progress: next};
        });
    }
    handleBack () {
        this.clearTimer();
        this.setState({phase: 'pick', transport: null, progress: 0});
    }
    handleCancel () {
        this.clearTimer();
        this.props.onClose();
    }
    render () {
        return (
            <DeviceConnectionModalComponent
                deviceName={this.props.deviceName}
                deviceType={this.props.deviceType}
                form={this.state.form}
                phase={this.state.phase}
                progress={this.state.progress}
                transport={this.state.transport}
                onBack={this.handleBack}
                onCancel={this.handleCancel}
                onFormChange={this.handleFormChange}
                onPickTransport={this.handlePickTransport}
                onStartConnect={this.handleStartConnect}
            />
        );
    }
}

DeviceConnectionModal.propTypes = {
    deviceName: PropTypes.string,
    deviceType: PropTypes.oneOf(['robot', 'kit']).isRequired,
    onClose: PropTypes.func.isRequired
};

export default DeviceConnectionModal;
